import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import React, {useContext, useEffect, useRef, useState} from "react";
import {Modal} from "../../../Smart/Modal/Modal";
import {SelectBox} from "../../../Pickers/SelectBox/SelectBox";
import {SearchableSelectBox} from "../../../Pickers/SearchableSelectBox/SearchableSelectBox";
import './CreateAutomationModal.scss'
import {createOrUpdateNotificationAutomationAPI, getCompanySMSQuotaAPI, userCountToSendSMSAPI} from "../../../../Services/api";
import {get} from "lodash";
import {FormInput} from "../../../Form/Input/FormInput";
import {AutomationSubTypes, MessageTypes, FeatureFlags} from "../../../../Utils/Constants";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {MessageTypeSelectionButtons} from "../../MessageTypeSelectionButtons/MessageTypeSelectionButtons";
import {GridReactContext} from "../../../../Utils/DataGridWrapper/GridContext";
import {useUserAdminOrgs} from "../../../Hooks/UseUserAdminOrgs";
import {getTaskOptionsForNotificationsInOrganization} from "../../../Settings/Reminders/RemindersLib";
import {SendNotificationCounts} from "../../SendNotificationCounts/SendNotificationCounts";
import {UserReactContext} from "../../../../contexts/UserContext";
import {CompanyReactContext} from "../../../../contexts/CompanyContext";
import {EntityTypes, RecurringFrequencies, SchedulingModes, countMessagesToSend} from "../../NotificationsLib";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Box} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import {he} from "date-fns/locale";
import {convertLocalTimeToUTC, convertUTCTimeToLocal} from "../../../../Utils/timezoneUtils";


const getHoursOptions = () => [
    {
        value: '10:00', text: '10:00'
    },
    {
        value: '11:00', text: '11:00'
    },
    {
        value: '12:00', text: '12:00'
    },
    {
        value: '13:00', text: '13:00'
    },
    {
        value: '14:00', text: '14:00'
    },
    {
        value: '15:00', text: '15:00'
    },
    {
        value: '16:00', text: '16:00'
    },
    {
        value: '17:00', text: '17:00'
    },
    {
        value: '18:00', text: '18:00'
    },
    {
        value: '19:00', text: '19:00'
    }

]
/**
 * CreateAutomationModal component - modal for creating and editing automation tasks
 * @param {object} props - Component props
 * @param {boolean} props.automationModalOpen - Whether the modal is open
 * @param {function} props.closeAutomationModal - Function to close the modal
 * @param {object} [props.automation] - Automation object for editing (optional)
 * @returns {JSX.Element} CreateAutomationModal component
 */
export const CreateAutomationModal = ({automationModalOpen, closeAutomationModal, automation}) => {
    const {adminOrganizationOptions, adminOrganizations, isOrgsLoading} = useUserAdminOrgs({
        withAllOption: !automation || get(automation, 'organizationIds', []).length > 1,
        withCoursesAndSignDocs: true
    })
    const {i18n} = useContext(I18nReactContext)
    const gridContext = useContext(GridReactContext);
    const userContext = useContext(UserReactContext);
    const companyContext = useContext(CompanyReactContext);
    const [selectedOrgOption, setSelectedOrgOption] = useState(get(automation, 'organizationIds', []).length === 1 ? get(automation, 'organizationIds', [])[0] : 'all')
    const [selectedTaskOption, setSelectedTaskOption] = useState(get(automation, 'signDocIds', []).length === 1 ? get(automation, 'signDocIds', [])[0] : 'all')
    const [signDocsOptions, setSignDocsOptions] = useState([])
    const [selectedHour, setSelectedHour] = useState(() => {
        const storedHour = get(automation, 'runByEventRelativeTime.runHour');
        // Convert stored UTC time to local time for display
        return storedHour ? convertUTCTimeToLocal(storedHour) : getHoursOptions()[0].value;
    })
    const [daysBefore, setDaysBefore] = useState(get(automation, 'runByEventRelativeTime.runDaysBefore') || 5)
    const [loading, setLoading] = useState(false)
    const [automationName, setAutomationName] = useState(get(automation, 'name', ''))
    const [automationDescription, setAutomationDescription] = useState(get(automation, 'description', ''))
    const [selectedMessagingTypes, setSelectedMessagingTypes] = useState(() => {
        if (!automation) return [MessageTypes.SMS];
        const type = get(automation, 'type', '');
        return type.includes(',') ? type.split(',') : [type];
    })
    const [isEditMode] = useState(!!automation)
    const [loadingSMSParameters, setLoadingSMSParameters] = useState(false)
    const [usersToSend, setUsersToSend] = useState({
        sendToUsersWithPhone: 0,
        sendToUsersWithEmail: 0,
        totalUsersCount: 0
    })
    const [companySMSQuota, setCompanySMSQuota] = useState(null)
    const oneTimeIgnoreCourseReset = useRef(true)
    const [automationType, setAutomationType] = useState(() => {
        const defaultType = get(automation, 'schedulingMode', SchedulingModes.RELATIVE_TO_TASK_END)
        // If recurring automation is disabled and user tries to use recurring, fallback to one-time
        if (
            defaultType === SchedulingModes.RECURRING_SCHEDULE &&
            !get(companyContext, ['featureFlags', FeatureFlags.RECURRING_AUTOMATION], false)
        ) {
            return SchedulingModes.RELATIVE_TO_TASK_END
        }
        return defaultType
    })
    const [recurringFrequency, setRecurringFrequency] = useState(get(automation, 'recurringFrequency', RecurringFrequencies.DAILY) || RecurringFrequencies.DAILY)
    const [recurringFrequencyValue, setRecurringFrequencyValue] = useState(get(automation, 'recurringFrequencyValue', 1) || 1)
    const [startDate, setStartDate] = useState(get(automation, 'startDate', new Date()) || new Date())

    const getRecurringFrequencyOptions = () => [
        {
            value: RecurringFrequencies.DAILY, text: String(i18n.translate('automations.createAutomationModal.recurring.daily'))
        },
        {
            value: RecurringFrequencies.WEEKLY, text: String(i18n.translate('automations.createAutomationModal.recurring.weekly'))
        },
        {
            value: RecurringFrequencies.MONTHLY, text: String(i18n.translate('automations.createAutomationModal.recurring.monthly'))
        }
    ]

    

    /**
     * Handle automation type change (one-time vs recurring)
     * @param {object} event - The event object
     */
    const handleAutomationTypeChange = (event) => {
        setAutomationType(event.target.value)
    }

    /**
     * Handle recurring frequency change
     * @param {object} event - The event object
     */
    const handleRecurringFrequencyChange = (event) => {
        setRecurringFrequency(event.target.value)
        if (event.target.value === RecurringFrequencies.DAILY && recurringFrequencyValue > 365) {
            setRecurringFrequencyValue(365)
        }
        if (event.target.value === RecurringFrequencies.WEEKLY && recurringFrequencyValue > 52) {
            setRecurringFrequencyValue(52)
        }
        if (event.target.value === RecurringFrequencies.MONTHLY && recurringFrequencyValue > 12) {
            setRecurringFrequencyValue(12)
        }
    }

    /**
     * Handle recurring frequency value change
     * @param {object} event - The event object
     */
    const handleRecurringFrequencyValueChange = (event) => {
        const value = parseInt(event.target.value) || 1
        if (recurringFrequency === RecurringFrequencies.DAILY) {
            if (value >= 1 && value <= 365) {
                setRecurringFrequencyValue(value)
            }
        } else if (recurringFrequency === RecurringFrequencies.WEEKLY) {
            if (value >= 1 && value <= 52) {
                setRecurringFrequencyValue(value)
            }
        } else {
            if (value >= 1 && value <= 12) {
                setRecurringFrequencyValue(value)
            }
        }
    }

    /**
     * Handle run hour change for recurring automations
     * @param {object} event - The event object
     */
    const handleRunHourChange = (event) => {
        setSelectedHour(event.target.value)
    }

    /**
     * Handle days before input change with validation
     * @param {object} e - Event object
     */
    const handleDaysBeforeInputChange = (e) => {
        //limit value to be between 1 and 50
        if (e.target.value < 1) {
            e.target.value = 1
        }
        if (e.target.value > 50) {
            e.target.value = 50
        }
        //remove non digit notes
        e.target.value = e.target.value.replace(/\D/g, '')
        setDaysBefore(e.target.value)
    }

    /**
     * Handle messaging type selection changes
     * @param {string} messagingType - The selected messaging type
     */
    const onSelectedMessagingTypeChange = (messagingType) => {
        if (selectedMessagingTypes.includes(messagingType) && selectedMessagingTypes.length > 1) {
            setSelectedMessagingTypes(selectedMessagingTypes.filter((type) => type !== messagingType))
        } else {
            let messagingTypes = [...selectedMessagingTypes, messagingType]
            //uniq array
            messagingTypes = messagingTypes.filter((value, index, self) => self.indexOf(value) === index)
            setSelectedMessagingTypes(messagingTypes)
        }
    }

    /**
     * Asynchronously loads and updates SMS-related parameters for the current automation configuration.
     * This function fetches two main pieces of information:
     * 1. The count of users who would receive SMS notifications based on the currently
     *    selected SignDoc task (`selectedTaskOption`) and organization (`selectedOrgOption`).
     *    This count considers various factors like users with phone numbers, users with email,
     *    and the total user count for the selected task.
     * 2. The company's remaining SMS quota, retrieved from the backend.
     *
     * The function depends on `selectedTaskOption` and `selectedOrgOption` being valid.
     * It updates the `loadingSMSParameters` state to manage UI loading states,
     * and sets the `companySMSQuota` and `usersToSend` state variables with the fetched data.
     *
     * @async
     * @returns {Promise<void>}
     */
    const loadSMSParameters = async () => {
        if (!selectedTaskOption || !selectedOrgOption) {
            return
        }
        // The entity type is explicitly set to SignDoc for this modal's context.
        const entityType = EntityTypes.SIGN_DOC
        setLoadingSMSParameters(true)
        const {
            sendToUsersWithPhone,
            sendToUsersWithEmail,
            totalUsersCount
        } = await userCountToSendSMSAPI({
            entityId: selectedTaskOption,
            entityType,
            organizationId: selectedOrgOption,
            skipAlreadyNotifiedUsers: false,
            messagingTypes: selectedMessagingTypes,
            allUsersWithOpenTask: selectedTaskOption === 'all' ? true : false
        })
        const {remainingQuota} = await getCompanySMSQuotaAPI({companyId: userContext.getUserCompanyId()})
        setCompanySMSQuota(remainingQuota)
        setUsersToSend({sendToUsersWithPhone, sendToUsersWithEmail, totalUsersCount})
        setLoadingSMSParameters(false)
    }
    useEffect(() => {
        loadSMSParameters()
    }, [selectedTaskOption]);


    const onSelectedCourseOptionChange = (e) => {
        setSelectedTaskOption(e.target.value)
    }
    const onSelectedOrgOptionChange = (e) => {
        setSelectedOrgOption(e.target.value)
    }
    const onAutomationNameChange = (e) => {
        setAutomationName(e.target.value)
    }
    const onAutomationDescriptionChange = (e) => {
        setAutomationDescription(e.target.value)
    }
    const setsignDocsOptions = async () => {
        const {signDocOptionsTmp} = await getTaskOptionsForNotificationsInOrganization({
            selectedOrgOption,
            adminOrganizations,
            showPastSignDocs: true,
            i18n,
            addAllOption: true
        });
        setSignDocsOptions(signDocOptionsTmp || [])
    }

    useEffect(() => {
        setsignDocsOptions()
        if (isEditMode && oneTimeIgnoreCourseReset.current) {
            return
        }
        setSelectedTaskOption("")
        oneTimeIgnoreCourseReset.current = false
    }, [selectedOrgOption, adminOrganizations, isOrgsLoading])

    /**
     * Consolidated validation function for all automation types
     * @param {boolean} ignoreFactorsWhichDoNotEffectCount - Whether to ignore factors that don't affect count
     * @returns {string|boolean} Error message or false if no errors
     */
    const getError = (ignoreFactorsWhichDoNotEffectCount) => {
        if (!selectedOrgOption) {
            return String(i18n.translate('automations.createAutomationModal.organizationOptions.error'))
        }
        if (!selectedTaskOption) {
            return String(i18n.translate('automations.createAutomationModal.taskOptions.error'))
        }
        if (ignoreFactorsWhichDoNotEffectCount) {
            return false
        } else {
            if (!automationName) {
                return String(i18n.translate('automations.createAutomationModal.nameError'))
            }
            
            if (automationType === SchedulingModes.RELATIVE_TO_TASK_END) {
                if (!selectedHour) {
                    return String(i18n.translate('automations.createAutomationModal.selectHourError'))
                }
                if (!isDaysBeforeValid()) {
                    return String(i18n.translate('automations.createAutomationModal.daysBeforeError'))
                }
            } else {
                // Recurring automation validation
                if (!startDate) {
                    return String(i18n.translate('automations.createAutomationModal.recurring.startDateRequired'))
                } else {
                    if (startDate < (new Date(new Date().setHours(0, 0, 0, 0)))) {
                        return String(i18n.translate('automations.createAutomationModal.recurring.startDatePast'))
                    }
                }
                if (!selectedHour) {
                    return String(i18n.translate('automations.createAutomationModal.recurring.runHourRequired'))
                }
            }

            // Quota check last, so a half-filled form reports its own problem first.
            // Deliberately only in this branch: canSend() calls getError(true), and
            // SendNotificationCounts uses canSend() to decide whether to render the
            // "not enough SMS" warning. Checking quota there would make the warning
            // hide itself, leaving the admin with a greyed-out button and no reason.
            if (!hasEnoughQuota()) {
                return String(i18n.translate('sendSMSModal.error.notEnoughSMS'))
            }
        }

        return false
    }

    /**
     * Whether the company can pay for every message this automation would send.
     * Counts are still being fetched while loadingSMSParameters is true, so the
     * numbers in hand belong to the previously selected task or organization.
     *
     * @returns {boolean} True when the company has enough quota to save
     */
    const hasEnoughQuota = () => {
        if (loadingSMSParameters) {
            return false
        }

        const messagesToSendCount = countMessagesToSend({
            messagingTypes: selectedMessagingTypes,
            sendToUsersWithPhone: get(usersToSend, 'sendToUsersWithPhone', 0),
            sendToUsersWithEmail: get(usersToSend, 'sendToUsersWithEmail', 0)
        })

        return companySMSQuota >= messagesToSendCount
    }
    const canSend = () => {
        return !getError(true)
    }
    const isDaysBeforeValid = () => {
        const daysBeforeInt = parseInt(daysBefore)
        return daysBeforeInt >= 1 && daysBeforeInt <= 50
    }

    const createNotificationAutomation = () => {
        setLoading(true)

        createOrUpdateNotificationAutomationAPI({ 
            name: automationName,
            description: automationDescription,
            runHour: convertLocalTimeToUTC(selectedHour),
            types: selectedMessagingTypes,
            signDocIds: [selectedTaskOption],
            organizationIds: [selectedOrgOption],
            automationId: automation?.id,
            schedulingMode: automationType,
            recurringFrequency,
            recurringFrequencyValue,
            startDate,
            runDaysBefore: parseInt(daysBefore)
        })
            .then(() => {
                closeAutomationModal()
            })
            .catch((error) => {
                console.error('Error creating automation:', error)
            })
            .finally(() => {
                setLoading(false)
                gridContext.triggerTableRefresh()
            })
    }

    /**
     * Generate preview text for recurring automation
     * @returns {string} Preview text
     */
    const getRecurringPreview = () => {
        if (automationType !== SchedulingModes.RECURRING_SCHEDULE) return ''
        
        // Ensure we have valid values before proceeding
        if (!recurringFrequency || !selectedHour || !recurringFrequencyValue) {
            return ''
        }
        
        const frequencyText = {
            [RecurringFrequencies.DAILY]: `${recurringFrequencyValue === 1 ?
                i18n.translate('automations.createAutomationModal.recurring.dailySingle') :
                `${recurringFrequencyValue} ${i18n.translate('automations.createAutomationModal.recurring.dailyPlural')}`
            }`,
            [RecurringFrequencies.WEEKLY]: `${recurringFrequencyValue === 1 ?
                i18n.translate('automations.createAutomationModal.recurring.weeklySingle') :
                `${recurringFrequencyValue} ${i18n.translate('automations.createAutomationModal.recurring.weeklyPlural')}`
            }`,
            [RecurringFrequencies.MONTHLY]: `${recurringFrequencyValue === 1 ?
                i18n.translate('automations.createAutomationModal.recurring.monthlySingle') :
                `${recurringFrequencyValue} ${i18n.translate('automations.createAutomationModal.recurring.monthlyPlural')}`
            }`
        }
        // Create preview text with fallback
        const currentFrequencyText = frequencyText[recurringFrequency] || `${recurringFrequencyValue} ${recurringFrequency}`
        
        // Use the translation function with proper parameter passing
        return String(i18n.translate('automations.createAutomationModal.modalRecurringPreview', { context: {
            frequencyText: currentFrequencyText, 
            runHour: selectedHour, 
        }}))
    }   

    return <Modal
        open={automationModalOpen}
        onClose={closeAutomationModal}>
        <div className="createAutomationModal modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('automations.createAutomationModal.title')}</div>
            </div>
            <div className="modalContent">
                {loading ? <LoadingSpinner/> : ""}
                <div className="automationTypeSection">
                    <div className="automation-type-tabs">
                        <RadioGroup
                            value={automationType}
                            onChange={handleAutomationTypeChange}
                            row
                            className="automation-type-radio-group"
                        >
                            <FormControlLabel 
                                value={SchedulingModes.RELATIVE_TO_TASK_END} 
                                control={<Radio sx={{ display: 'none' }} />} 
                                label={i18n.translate('automations.createAutomationModal.automationType.oneTime')}
                                className="automation-type-tab"
                            />
                            <FormControlLabel 
                                value={SchedulingModes.RECURRING_SCHEDULE} 
                                control={<Radio sx={{ display: 'none' }} />} 
                                label={i18n.translate('automations.createAutomationModal.automationType.recurring')}
                                disabled={!get(companyContext, ['featureFlags', FeatureFlags.RECURRING_AUTOMATION], false)}  
                                className="automation-type-tab"
                            />
                        </RadioGroup>
                    </div>
                </div>

                <div className="selectBoxes">
                    <div className="nameAndDescriptionInputs">
                        <div className="nameInput">
                            <FormInput
                                required
                                dontShowErrorMessage
                                id="name"
                                label={String(i18n.translate('automations.createAutomationModal.nameLabel'))}
                                className="login-text-field first"
                                value={automationName}
                                onChange={onAutomationNameChange}
                            />
                        </div>
                        <div className="descriptionInput">
                            <FormInput
                                dontShowErrorMessage
                                id="description"
                                label={String(i18n.translate('automations.createAutomationModal.descriptionLabel'))}
                                className="login-text-field first"
                                value={automationDescription}
                                onChange={onAutomationDescriptionChange}
                            />
                        </div>
                    </div>
                    <div className="selectCourseAndOrg">
                        <div className="selectOrg">
                            {adminOrganizationOptions ?
                                <SelectBox
                                    key="selectOrg"
                                    options={adminOrganizationOptions} value={selectedOrgOption}
                                    label={String(i18n.translate('automations.createAutomationModal.organizationOptions.label'))}
                                    onChange={onSelectedOrgOptionChange}/> : ""}
                        </div>
                        <div className="selectCourse">
                            {signDocsOptions?.length ? <SearchableSelectBox
                                key="selectCourse"
                                options={signDocsOptions}
                                value={selectedTaskOption}
                                label={String(i18n.translate('automations.createAutomationModal.taskOptions.label'))}
                                dataTestId="automation-task-select"
                                onChange={onSelectedCourseOptionChange}/> : ""}
                        </div>
                    </div>
                    {automationType === SchedulingModes.RELATIVE_TO_TASK_END ? (
                        <div className="selectHourAndDaysBefore">
                            <div className="selectHour">
                                <SelectBox
                                    key="selectHour"
                                    options={getHoursOptions()} value={selectedHour}
                                    label={String(i18n.translate('automations.createAutomationModal.selectHourLabel'))}
                                    onChange={handleRunHourChange}/>
                            </div>
                            <div className="daysBeforeInput">
                                <FormInput
                                    dontShowErrorMessage
                                    disabled={false}
                                    title=""
                                    onKeyDown={() => {}}
                                    onKeyUp={() => {}}
                                    id="daysBefore"
                                    label={String(i18n.translate('automations.createAutomationModal.howManyDaysBefore'))}
                                    className="login-text-field first"
                                    value={daysBefore}
                                    onChange={handleDaysBeforeInputChange}
                                    type="number"
                                    inputProps={{
                                        maxLength: 2,
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*'
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="recurringOptions">
                            <div className="recurringFrequencySection">
                                <div className="frequencySelector">
                                    <SelectBox
                                        key="recurringFrequency"
                                        options={getRecurringFrequencyOptions()}
                                        value={recurringFrequency}
                                        label={String(i18n.translate('automations.createAutomationModal.recurring.frequency'))}
                                        onChange={handleRecurringFrequencyChange}
                                    />
                                </div>
                                <div className="frequencyValueInput">
                                    <FormInput
                                        dontShowErrorMessage
                                        disabled={false}
                                        title=""
                                        onKeyDown={() => {}}
                                        onKeyUp={() => {}}
                                        id="frequencyValue"
                                        label={String(i18n.translate('automations.createAutomationModal.recurring.every'))}
                                        className="login-text-field first"
                                        value={recurringFrequencyValue}
                                        onChange={handleRecurringFrequencyValueChange}
                                        type="number"
                                        inputProps={{
                                            min: 1,
                                            max: recurringFrequency === RecurringFrequencies.DAILY ? 365 : recurringFrequency === RecurringFrequencies.WEEKLY ? 52 : 12,
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Date Range */}
                            <div className="dateRangeSection">
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                                    <div className="datePickerWrapper">
                                        <DatePicker
                                            label={i18n.translate('automations.createAutomationModal.recurring.startDate')}
                                            value={startDate}
                                            onChange={setStartDate}
                                            disablePast
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: 'outlined'
                                                }
                                            }}
                                        />
                                    </div>
                                </LocalizationProvider>
                            </div>

                            {/* Max Runs and Run Hour */}
                            <div className="runsAndTimeSection">
                                <div className="runHourSection">
                                    <SelectBox
                                        key="runHour"
                                        options={getHoursOptions()}
                                        value={selectedHour}
                                        label={String(i18n.translate('automations.createAutomationModal.recurring.runHour'))}
                                        onChange={handleRunHourChange}
                                    />
                                </div>
                            </div>
                            {automationType === SchedulingModes.RECURRING_SCHEDULE && (
                                <div className="previewSection">
                                    <div className="previewTitle">
                                        {i18n.translate('automations.createAutomationModal.recurring.preview')}
                                    </div>
                                    <div className="previewBox">
                                        <strong>{getRecurringPreview()}</strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                
                <div className="messagingTypeWrapper">
                    <MessageTypeSelectionButtons onSelectedMessageTypeChange={onSelectedMessagingTypeChange}
                                                 selectedMessagingTypes={selectedMessagingTypes}/>
                </div>
                <SendNotificationCounts 
                    canSend={canSend} 
                    loadingSMSParameters={loadingSMSParameters}
                    usersToSend={usersToSend}
                    messagingTypes={selectedMessagingTypes}
                    companySMSQuota={companySMSQuota} 
                    selectedSentToOption=""
                />
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.GREY}
                    onClick={closeAutomationModal}
                    text={String(i18n.translate('general.cancel'))}
                />
                <Button
                    color={buttonColors.YELLOW}
                    onClick={createNotificationAutomation}
                    disabled={!!getError(false)}
                    text={isEditMode ? String(i18n.translate('general.saveChanges')) : String(i18n.translate('automations.createAutomationModal.create'))}
                />

            </div>
        </div>
    </Modal>
}